<script setup>
import { h, ref, computed, onMounted } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import {
  stripBlockedMenuItems,
  getBlockedFeatureKeys,
  flattenCatalogKeys,
} from 'dashboard/constants/accessControl.js';
import { useRestrictedFeatures } from 'dashboard/composables/useRestrictedFeatures';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnSegueCloud } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const currentUserRole = useMapGetter('getCurrentRole');
const currentUser = useMapGetter('getCurrentUser');
// "Horario de Uso" / "Controle de Acesso": itens visiveis so para o super admin (id 1).
const isSuperAdmin = computed(() => currentUser.value?.id === 1);
// FUNCOES RESTRITAS (v87): Horario de Uso, menu Meta e WhatsApp ao vivo sao
// liberados POR USUARIO na secao Funcoes Restritas do Controle de Acesso
// (so id 1 gerencia). Flags do usuario logado vem do backend.
const { restrictedFlags } = useRestrictedFeatures(accountId.value);
const canSeeHorarioUso = computed(
  () => isSuperAdmin.value || !!restrictedFlags.value?.horario_uso
);
const canSeeMeta = computed(
  () => isSuperAdmin.value || !!restrictedFlags.value?.meta_menu
);
const canSeeLiveWhatsApp = computed(
  () => isSuperAdmin.value || !!restrictedFlags.value?.live_whatsapp
);
// v44 Controle de Acesso: keys de menu/funcao bloqueadas para o usuario logado.
// id 1 nunca e restringido (retorna []).
const accessBlocklist = computed(() => getBlockedFeatureKeys(currentUser.value));
// Keys configuraveis na tela /controle-de-acesso (fonte do bypass do filtro de roles).
const catalogKeys = computed(() => new Set(flattenCatalogKeys()));
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

// OCULTAR CAIXA DE ENTRADA (v54): caixas hidden=true somem da lista "Canais"
// para TODOS, inclusive o super admin (id 1). O backend ja remove as ocultas
// da API para os demais; este filtro fecha o caso do id 1, que recebe todas.
const sortedInboxes = computed(() =>
  inboxes.value
    .filter(inbox => !inbox.hidden)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  const base = [
    // Dashboard — primeiro item do menu. v90: virou GRUPO com sub-páginas
    // (mesmo padrão do menu Configurações): Visão Geral, Ofertas, Contratos
    // e Equipe.
    {
      name: 'SegueDashboard',
      label: t('SIDEBAR.SEGUE_DASHBOARD'),
      icon: 'i-lucide-layout-dashboard',
      // v105: sem trava de papel — quem decide é o Controle de Acesso
      // (ui_blocklist; backfill bloqueou os perfis que não viam antes)
      children: [
        {
          name: 'SegueDashboard VisaoGeral',
          label: 'Visão Geral',
          icon: 'i-lucide-layout-dashboard',
          to: accountScopedRoute('segue_dashboard_visao'),
          activeOn: ['segue_dashboard_visao', 'segue_dashboard'],
        },
        {
          name: 'SegueDashboard Ofertas',
          label: 'Ofertas',
          icon: 'i-lucide-circle-dollar-sign',
          to: accountScopedRoute('segue_dashboard_ofertas'),
          activeOn: ['segue_dashboard_ofertas'],
        },
        {
          name: 'SegueDashboard Contratos',
          label: 'Contratos',
          icon: 'i-lucide-file-text',
          to: accountScopedRoute('segue_dashboard_contratos'),
          activeOn: ['segue_dashboard_contratos'],
        },
        {
          name: 'SegueDashboard Equipe',
          label: 'Equipe',
          icon: 'i-lucide-users',
          to: accountScopedRoute('segue_dashboard_equipe'),
          activeOn: ['segue_dashboard_equipe'],
        },
      ],
    },
    // v82: menu "Contatos Realtime" (/reports/live) REMOVIDO a pedido do
    // Kairo 04/07/2026 — a rota continua existindo, so saiu do menu.
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            icon: h(ChannelIcon, { inbox, class: 'size-[16px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    // v107: o item "Simulador" (o ANTIGO, que abria o SimuladorModal com ficha
    // vazia) saiu do menu junto com a rota simulador_index — decisão do Kairo.
    // Quem simula e digita agora é só "Simulação e Digitação", que usa o
    // simulador-api e as credenciais por caixa.
    // Telas portadas do MAZAM AI.
    {
      name: 'Simulacao Digitacao',
      label: 'Simulação e Digitação',
      icon: 'i-lucide-file-pen-line',
      to: accountScopedRoute('simulacao_digitacao_index'),
      activeOn: ['simulacao_digitacao_index'],
    },
    {
      name: 'Propostas',
      label: 'Propostas',
      icon: 'i-lucide-scroll-text',
      to: accountScopedRoute('propostas_index'),
      activeOn: ['propostas_index'],
    },
    {
      name: 'Captain',
      label: 'Captain',
      icon: 'i-lucide-bot',
      // v105: sem trava de papel — Controle de Acesso decide
      to: accountScopedRoute('captain_assistants_index', {
        navigationPath: 'captain_assistants_responses_index',
      }),
      activeOn: [
        'captain_assistants_index',
        'captain_assistants_responses_index',
        'captain_assistants_documents_index',
        'captain_tools_index',
        'captain_assistants_scenarios_index',
        'captain_assistants_playground_index',
        'captain_assistants_inboxes_index',
        'captain_assistants_responses_pending',
        'captain_assistants_settings_index',
        'captain_assistants_create_index',
      ],
    },
    {
      name: 'Kanban',
      label: t('SIDEBAR.KANBAN'),
      icon: 'i-lucide-kanban-square',
      to: accountScopedRoute('kanban_index'),
      activeOn: ['kanban_index'],
      permissions: [
        'administrator',
        'supervisor',
        'agent',
        'conversation_manage',
        'conversation_unassigned_manage',
        'conversation_participating_manage',
      ],
    },
    // Menu Meta — id 1 + e-mails autorizados (MetaAccess no backend).
    // Abaixo do Kanban, com submenus Template / Disparo / Historico.
    ...(canSeeMeta.value
      ? [
          {
            name: 'Meta',
            label: t('SIDEBAR.META'),
            icon: 'i-lucide-message-square-share',
            roles: ['administrator'],
            children: [
              {
                name: 'Meta Template',
                label: t('SIDEBAR.META_TEMPLATE'),
                to: accountScopedRoute('meta_templates'),
              },
              {
                name: 'Meta Disparo',
                label: t('SIDEBAR.META_DISPARO'),
                to: accountScopedRoute('meta_disparo'),
              },
              {
                name: 'Meta Historico',
                label: t('SIDEBAR.META_HISTORICO'),
                to: accountScopedRoute('meta_historico'),
              },
            ],
          },
        ]
      : []),
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      // v105: sem trava de papel — Controle de Acesso decide
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: 'i-lucide-building-2',
      // v105: sem trava de papel — Controle de Acesso decide
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index'],
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      // v105: sem trava de papel — Controle de Acesso decide
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },
    // v106: Ranking de vendas (pódium dos contratos pagos). Nasce bloqueado
    // p/ não-admins (backfill) — Kairo libera no Controle de Acesso.
    {
      name: 'Ranking',
      label: 'Ranking',
      icon: 'i-lucide-trophy',
      to: accountScopedRoute('ranking_index'),
      activeOn: ['ranking_index'],
    },
    // v109: WhatsApp Call — ligações pelo número oficial. Nasce bloqueado
    // p/ não-admins (backfill); chave geral do sistema fica na Configuração.
    {
      name: 'WhatsappCall',
      label: 'WhatsApp Call',
      icon: 'i-lucide-phone-call',
      children: [
        {
          name: 'WhatsappCall Dashboard',
          label: 'Dashboard',
          icon: 'i-lucide-chart-no-axes-column',
          to: accountScopedRoute('whatsapp_call_dashboard'),
          activeOn: ['whatsapp_call_dashboard'],
        },
        {
          name: 'WhatsappCall Config',
          label: 'Configuração',
          icon: 'i-lucide-settings-2',
          to: accountScopedRoute('whatsapp_call_config'),
          activeOn: ['whatsapp_call_config'],
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      // v105: sem trava de papel — Controle de Acesso decide
      // ("WhatsApp ao vivo" continua nas Funções Restritas)
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
        ...(canSeeLiveWhatsApp.value
          ? [
              {
                name: 'WhatsApp ao vivo',
                label: t('SIDEBAR.LIVE_WHATSAPP'),
                to: accountScopedRoute('campaigns_live_whatsapp_index'),
              },
            ]
          : []),
      ],
    },
    // Horario de Uso — super admin (id 1) + e-mails autorizados (mesma lista do
    // guard no HorarioUsoController). Posicionado abaixo de Campanhas.
    ...(isSuperAdmin.value || canSeeHorarioUso.value
      ? [
          {
            name: 'HorarioUso',
            label: t('SIDEBAR.HORARIO_USO'),
            icon: 'i-lucide-calendar-clock',
            to: accountScopedRoute('horario_uso'),
            activeOn: ['horario_uso'],
            roles: ['administrator'],
          },
        ]
      : []),
    // Controle de Acesso — somente super admin (id 1).
    ...(isSuperAdmin.value
      ? [
          {
            name: 'ControleAcesso',
            label: t('SIDEBAR.CONTROLE_ACESSO'),
            icon: 'i-lucide-shield-check',
            to: accountScopedRoute('controle_acesso'),
            activeOn: ['controle_acesso'],
            roles: ['administrator'],
          },
        ]
      : []),
    // Help Center menu hidden
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      // v105: sem trava de papel — Controle de Acesso decide (backfill
      // bloqueou todos os não-admins; API de vários submenus segue admin-only)
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        // v110: chave geral do chat interno (liga/desliga o balão pra todos)
        {
          name: 'Settings Chat Interno',
          label: 'Chat Interno',
          icon: 'i-lucide-message-circle',
          to: accountScopedRoute('chat_interno_settings_index'),
          activeOn: ['chat_interno_settings_index'],
        },
        {
          name: 'Settings Captain',
          label: t('SIDEBAR.CAPTAIN_AI'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('captain_settings_index'),
        },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_show',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
          to: accountScopedRoute('settings_teams_list'),
        },
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-lucide-user-cog',
                activeOn: [
                  'assignment_policy_index',
                  'agent_assignment_policy_index',
                  'agent_assignment_policy_create',
                  'agent_assignment_policy_edit',
                  'agent_capacity_policy_index',
                  'agent_capacity_policy_create',
                  'agent_capacity_policy_edit',
                ],
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          activeOn: [
            'settings_inbox_list',
            'settings_inbox_show',
            'settings_inbox_new',
            'settings_inbox_finish',
            'settings_inboxes_page_channel',
            'settings_inboxes_add_agents',
          ],
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        // Task 5 (credenciais por caixa, 11/08/2026): o item "Credenciais de
        // Bancos" (conta inteira) saiu do menu — a tela virou a aba
        // "Credenciais dos Bancos" DENTRO de cada caixa (Configurações da
        // Caixa), porque a credencial agora pertence a UMA inbox
        // (bank_credentials.inbox_id), não mais à conta. A rota
        // `bank_settings_index` continua existindo no código (não mexemos
        // nela: está fora do overlay, baked na imagem), mas sem link nenhum
        // apontando pra ela ninguém chega lá andando — e se chegar direto
        // pela URL, a tela hoje pede `inbox_id` e não tem de onde tirar, então
        // só mostra "Erro ao carregar os bancos" (sem crash, sem vazar dado).
        {
          name: 'Settings Other Settings',
          label: 'Credenciais e Chaves',
          icon: 'i-lucide-key-round',
          to: accountScopedRoute('other_settings_index'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Conversation Workflow',
          label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    },
  ].filter(item => {
    // Controle de Acesso MANDA: item QUE EXISTE NO CATALOGO (ou seja, e
    // configuravel na tela /controle-de-acesso) e que NAO esta bloqueado
    // (verde) aparece — mesmo com `roles: ['administrator']` fixo aqui.
    // Mesma regra do provider.js#resolvePermissions e do guard de rota.
    // ATENCAO: itens FORA do catalogo (Meta, HorarioUso, ControleAcesso,
    // WhatsApp ao vivo) NAO entram nesse bypass — eles seguem a regra nativa
    // (roles/isSuperAdmin/FuncoesRestritas), senao vazariam pra todo mundo,
    // inclusive a propria tela de Controle de Acesso.
    if (
      item.name &&
      catalogKeys.value.has(item.name) &&
      !accessBlocklist.value.includes(item.name)
    ) {
      return true;
    }
    // Se o item tem roles definidas, verificar se o usuário tem permissão
    if (item.roles && !item.roles.includes(currentUserRole.value)) {
      return false;
    }
    return true;
  });

  // v44 Controle de Acesso: remove os itens/sub-itens desmarcados para este usuario.
  return stripBlockedMenuItems(base, accessBlocklist.value);
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-n-background flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <!-- v78: menu recolhido — botao verde (seta direita) reabre o menu -->
          <div class="flex flex-col items-center gap-2">
            <SidebarAccountSwitcher
              is-collapsed
              @show-create-account-modal="emit('showCreateAccountModal')"
            />
            <button
              class="bg-n-teal-9 hover:bg-n-teal-10 text-white rounded-lg w-8 h-7 flex items-center justify-center shadow"
              title="Mostrar menu"
              @click="snapToExpanded"
            >
              <span class="i-lucide-arrow-right text-base" />
            </button>
          </div>
        </template>
        <template v-else>
          <div class="grid flex-shrink-0 place-content-center size-6">
            <Logo class="size-4" />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
          <!-- v78: botao verde (seta esquerda) recolhe o menu — pedido do Kairo -->
          <button
            class="bg-n-teal-9 hover:bg-n-teal-10 text-white rounded-lg w-8 h-7 flex items-center justify-center shrink-0 shadow ltr:mr-1"
            title="Ocultar menu"
            @click="snapToCollapsed"
          >
            <span class="i-lucide-arrow-left text-base" />
          </button>
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align-position="right" @close="onComposeClose">
          <template #trigger="{ toggle, isOpen }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
              @click="onComposeOpen(toggle)"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnSegueCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnSegueCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>
</template>
